import { useMemo } from 'react'
import {
  isEmpty,
  isNotEmpty,
  isTeamId,
  pickHighestPermissionScope,
  type PermissionEntityScope,
  type TeamData,
  type TeamId,
  type UserId,
} from '@terros/common'
import { useSafeCurrentWorkflow } from '../workflow/context'
import { useTeams } from '../team/list'
import { usePermissionMap } from '../permissions/map'
import { useProfile } from './get'

type TrailSecurityLevel = 'admin' | 'manager' | 'rep'

export type TrailSecurity = {
  level: TrailSecurityLevel
  isLoading: boolean
  defaultTeamId: TeamId | undefined
  currentUserId: UserId | undefined
  teamFilter: (team: TeamData) => boolean
  teamIds: TeamId[]
}

export function useTrailSecurity(): TrailSecurity {
  const { user, isLoading: isProfileLoading } = useProfile()
  const { data: teams, isLoading: isTeamsLoading } = useTeams()
  const accountListPermissionIds = usePermissionMap({ permissionType: 'account:list' })
  const workflowContext = useSafeCurrentWorkflow()

  const isLoading = isProfileLoading || isTeamsLoading

  const accountListScope = pickHighestPermissionScope(user?.permissionMap?.['account:list'])
  const permittedTeamIds = useMemo(() => new Set(accountListPermissionIds.filter(isTeamId)), [accountListPermissionIds])

  const workflowTeamIds = useMemo(() => {
    return workflowContext?.workflow.data.visibleTo?.filter(isTeamId) ?? []
  }, [workflowContext?.workflow.data.visibleTo])

  const isWorkflowTeamScoped = isNotEmpty(workflowTeamIds)

  const level = getTrailSecurityLevel(accountListScope?.entity)

  const defaultTeamId: TeamId | undefined = useMemo(() => {
    if (level !== 'manager') return
    const permittedTeams = teams?.filter((team) => permittedTeamIds.has(team.teamId))
    if (isEmpty(permittedTeams)) return
    return permittedTeams.sort((a, b) => a.level - b.level).at(0)?.teamId
  }, [level, permittedTeamIds, teams])

  const teamFilter = useMemo((): ((team: TeamData) => boolean) => {
    return (team: TeamData): boolean => {
      const hasSecurityAccess =
        level === 'admin' ||
        (level === 'manager' &&
          (accountListScope?.entity === 'withinRestrictions' || permittedTeamIds.has(team.teamId)))

      if (!hasSecurityAccess) return false
      if (!isWorkflowTeamScoped) return true
      if (workflowTeamIds.includes(team.teamId)) return true
      return team.parentTeamIds.some((parentTeamId) => workflowTeamIds.includes(parentTeamId))
    }
  }, [accountListScope?.entity, isWorkflowTeamScoped, level, permittedTeamIds, workflowTeamIds])

  const teamIds = useMemo((): TeamId[] => {
    return teams?.filter(teamFilter).map((team) => team.teamId) ?? []
  }, [teamFilter, teams])

  return {
    level,
    isLoading,
    defaultTeamId,
    currentUserId: user?.userId,
    teamFilter,
    teamIds,
  }
}

export function getTrailSecurityLevel(scope: PermissionEntityScope | undefined): TrailSecurityLevel {
  if (scope === 'company') return 'admin'
  if (scope === 'downline' || scope === 'withinRestrictions') return 'manager'
  return 'rep'
}
